// EntityDetailsDialog — markdown-editor + attachment-list för en entity
// (block / group / layer / connector). Öppnas via dubbelklick eller
// kontext-meny. Mobile får Sheet-version, desktop full Dialog.

import { useState, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Paperclip, Trash2, Download, Loader2, FileText, Eye, Pencil } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEntityDetailsCapabilities } from '@/hooks/useEntityDetailsCapabilities';
import { tierDisplayName } from '@/lib/planTiers';
import { Sparkles } from 'lucide-react';
import {
  useEntityDetails,
  useUpsertEntityDetails,
  useUploadAttachment,
  useDeleteAttachment,
  getAttachmentSignedUrl,
  fetchAttachmentText,
  type EntityType,
  type EntityAttachmentRow,
} from '@/hooks/useEntityDetails';
import { useWorkflowOptional } from '@/contexts/WorkflowContext';
import { toast } from '@/hooks/use-toast';

export interface EntityDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string | null;
  entityId: string | null;
  entityType: EntityType;
  /** Display-name visat i header. Faller tillbaka till entity_id om tomt. */
  entityName?: string;
}

export function EntityDetailsDialog({
  open, onOpenChange, workflowId, entityId, entityType, entityName,
}: EntityDetailsDialogProps) {
  const isMobile = useIsMobile();
  // Tier-gate: Free plan doesn't include the entity_details +
  // entity_attachments storage, so the dialog body is replaced with
  // an upsell card when the gate is closed. Hooks below still run
  // unconditionally to keep the hook order stable across renders.
  const detailsCaps = useEntityDetailsCapabilities();
  const enabled = !!workflowId && !!entityId && open && detailsCaps.entityDetailsEnabled;
  const { data, isLoading } = useEntityDetails(enabled ? workflowId : null, enabled ? entityId : null);
  const upsert = useUpsertEntityDetails();
  const upload = useUploadAttachment();
  const remove = useDeleteAttachment();

  const [draftMd, setDraftMd] = useState('');
  // Tracks whether we're currently downloading a bucket-backed body
  // so the UI can show a loader and the dialog title can flag it as
  // "Hämtar från bilaga…" instead of looking like an empty editor.
  const [hydratingBucket, setHydratingBucket] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // When `description_md` is empty AND metadata says the body was
  // routed to the bucket on save (`_detail_in_bucket: true`), pick
  // the matching attachment so we can fetch it lazily on dialog
  // open. Prefer the `${type}-${id}-detail.md` filename pattern that
  // `pushBucketAttachmentsForWorkflow` writes; fall back to the most
  // recently uploaded text/markdown attachment.
  const deferredAttachment = useMemo<EntityAttachmentRow | null>(() => {
    if (!data) return null;
    const meta = data.details?.metadata as Record<string, unknown> | undefined;
    if (!meta || meta._detail_in_bucket !== true) return null;
    if ((data.details?.description_md ?? '').length > 0) return null;

    const mdAttachments = data.attachments.filter(
      (a) => a.mime_type === 'text/markdown',
    );
    if (mdAttachments.length === 0) return null;
    const expectedPrefix = `${entityType}-${entityId ?? ''}-detail`;
    const named = mdAttachments.find((a) => a.filename.startsWith(expectedPrefix));
    if (named) return named;
    // Fallback: most recent by uploaded_at.
    return [...mdAttachments].sort((a, b) =>
      b.uploaded_at.localeCompare(a.uploaded_at),
    )[0];
  }, [data, entityType, entityId]);

  // In-memory fallback för detail-text: ENDAST `detailedDescription`.
  // Den korta `description` (block-kortets summering) är ett separat
  // fält och får INTE läcka in i denna dialog — annars blir det
  // omöjligt att hålla isär de två fälten (rapporterad bugg: "de ser
  // ut att vara samma fält"). Cloud-värdet
  // (entity_details.description_md) vinner alltid när det finns;
  // in-memory används bara som första-render-default.
  const wf = useWorkflowOptional();
  const inMemoryDetailedDescription = (() => {
    if (!entityId || entityType !== 'block' || !wf) return null;
    const block = wf.blocks.find((b) => b.id === entityId);
    return block?.config?.detailedDescription ?? null;
  })();

  // Synca draft från RPC:n bara när dialogen öppnas (inte vid varje
  // re-render) — annars skulle user:s in-progress-edits skrivas över när
  // useQuery returnerar samma data en gång till.
  useEffect(() => {
    if (!open) return;
    // Vänta på cloud-svaret innan vi sätter draften — annars skulle
    // in-memory-fallbacken hinna sätta värdet och sedan klubbas av cloud.
    if (isLoading) return;
    const cloudMd = data?.details?.description_md;
    if (cloudMd && cloudMd.length > 0) {
      setDraftMd(cloudMd);
      return;
    }
    // Bucket-backed body (over 1 MB on save) — fetch the markdown text
    // and prefill the editor so the user can read + edit inline.
    // Aborts cleanly if the dialog closes mid-fetch.
    if (deferredAttachment) {
      let cancelled = false;
      setHydratingBucket(true);
      setDraftMd('');
      void fetchAttachmentText(deferredAttachment.storage_path)
        .then((text) => {
          if (cancelled) return;
          if (text != null) setDraftMd(text);
          else if (inMemoryDetailedDescription) setDraftMd(inMemoryDetailedDescription);
        })
        .finally(() => {
          if (!cancelled) setHydratingBucket(false);
        });
      return () => {
        cancelled = true;
      };
    }
    if (inMemoryDetailedDescription && inMemoryDetailedDescription.length > 0) {
      setDraftMd(inMemoryDetailedDescription);
    } else {
      setDraftMd('');
    }
  }, [open, data, isLoading, inMemoryDetailedDescription, deferredAttachment]);

  const headerTitle = entityName?.trim() || entityId || '(unnamed)';

  // Upsell shown to free-plan users in place of the editor + attachments.
  // Pure render — no state, no side effects. Centralised so both the
  // Dialog and Sheet branches reuse the same copy.
  const upsellBody = (
    <div className="flex flex-col gap-3 py-2" data-testid="entity-details-upsell">
      <div className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="w-4 h-4 text-primary" />
          Detaljer + Bilagor är en betalfunktion
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Markdown-anteckningar per block, gruppen och kopplingen — plus
          bifogade filer — kräver moln-lagring som inte ingår i Free.
          Uppgradera till {tierDisplayName(detailsCaps.entityDetailsMinTier)}{' '}
          för att låsa upp.
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => {
              onOpenChange(false);
              // Full navigation (not useNavigate) so the dialog can
              // live outside a <Router> context — keeps existing
              // tests + standalone embeds working without a wrapper.
              if (typeof window !== 'undefined') {
                window.location.assign('/pricing');
              }
            }}
            data-testid="entity-details-upgrade-cta"
          >
            Uppgradera
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
            Stäng
          </Button>
        </div>
      </div>
    </div>
  );

  const handleSave = async () => {
    if (!entityId) return;
    // Always mirror to in-memory `config.detailedDescription` first so
    // the user's text is preserved even before the workflow has a
    // cloud row (currentDocumentId === null). Without this, hitting
    // Save on a brand-new unsaved workflow does nothing — silently
    // dropping the user's input.
    if (wf && entityType === 'block') {
      const block = wf.blocks.find((b) => b.id === entityId);
      if (block) {
        wf.updateBlock(entityId, {
          config: { ...block.config, detailedDescription: draftMd },
        });
      }
    }
    // Cloud sync — only when the workflow has been saved (has a
    // workflowId). Best-effort: never block the dialog or hide errors.
    if (workflowId) {
      try {
        // When the body was previously bucket-backed and the user is
        // now saving (regardless of whether the new content is short
        // or still long), the inline TEXT column becomes authoritative.
        // Strip the `_detail_in_bucket` flag from metadata so the
        // hydration effect doesn't re-fetch the stale attachment on
        // the next open. Other metadata keys are preserved.
        const existingMeta =
          (data?.details?.metadata as Record<string, unknown> | undefined) ?? {};
        // Filter rather than mutate so the original object stays
        // immutable (avoids stale-cache surprises in React Query).
        const {
          _detail_in_bucket: _b1,
          _detail_bytes: _b2,
          ...cleanMeta
        } = existingMeta;
        void _b1; void _b2;
        await upsert.mutateAsync({
          workflowId,
          entityId,
          entityType,
          descriptionMd: draftMd,
          metadata: cleanMeta,
        });
        // Remove the now-redundant bucket attachment so we don't keep
        // two copies of the body (one stale in storage, one fresh in
        // TEXT). Best-effort: a failed delete just leaves an orphan
        // that the user can clean up manually — doesn't break save.
        if (deferredAttachment) {
          try {
            await remove.mutateAsync({
              attachmentId: deferredAttachment.id,
              workflowId,
              entityId,
            });
          } catch {
            /* leave orphan; user can delete manually from the list. */
          }
        }
        toast({ title: 'Sparat' });
      } catch (err) {
        toast({
          title: 'Sparat lokalt — moln-synk misslyckades',
          description: err instanceof Error ? err.message : String(err),
          variant: 'destructive',
        });
      }
    } else {
      toast({
        title: 'Sparat lokalt',
        description:
          'Spara workflow till molnet för att synca detaljbeskrivningen och bilagor.',
      });
    }
  };

  const handleUpload = async (file: File) => {
    if (!workflowId || !entityId) return;
    await upload.mutateAsync({ workflowId, entityId, entityType, file });
  };

  const handleDeleteAttachment = async (att: EntityAttachmentRow) => {
    if (!workflowId || !entityId) return;
    await remove.mutateAsync({
      attachmentId: att.id, workflowId, entityId,
    });
  };

  // Dirty against whichever source the draft was hydrated from:
  // cloud row when one exists, otherwise the in-memory mirror. Using
  // ONLY the cloud value here would mark a draft as "not dirty" when
  // the user clears in-memory text on a workflow that has never been
  // saved to cloud — and the Save button would stay disabled.
  const dirty = workflowId
    ? draftMd !== (data?.details?.description_md ?? '')
    : draftMd !== (inMemoryDetailedDescription ?? '');

  const body = (
    <div className="flex flex-col gap-3">
      {isLoading && (
        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Laddar…
        </div>
      )}
      {!isLoading && hydratingBucket && (
        <div className="flex items-center gap-2 rounded border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Hämtar beskrivning från bilaga…
        </div>
      )}

      {!isLoading && (
        <>
          <Tabs defaultValue="edit">
            <TabsList className="h-8">
              <TabsTrigger value="edit" className="text-xs h-6 gap-1">
                <Pencil className="w-3 h-3" /> Redigera
              </TabsTrigger>
              <TabsTrigger value="preview" className="text-xs h-6 gap-1">
                <Eye className="w-3 h-3" /> Förhandsvisning
              </TabsTrigger>
            </TabsList>
            <TabsContent value="edit" className="mt-2">
              <Textarea
                value={draftMd}
                onChange={(e) => setDraftMd(e.target.value)}
                placeholder="Beskriv den här entiteten med Markdown…"
                className="min-h-[180px] font-mono text-xs"
              />
            </TabsContent>
            <TabsContent value="preview" className="mt-2">
              <div className="prose prose-sm dark:prose-invert max-w-none border rounded p-3 min-h-[180px]">
                {draftMd.trim()
                  ? <ReactMarkdown>{draftMd}</ReactMarkdown>
                  : <p className="text-muted-foreground italic">Ingen beskrivning ännu.</p>
                }
              </div>
            </TabsContent>
          </Tabs>

          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold flex items-center gap-1">
                <Paperclip className="w-3.5 h-3.5" />
                Bilagor ({data?.attachments.length ?? 0})
              </h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={upload.isPending}
                className="h-7 text-xs"
              >
                {upload.isPending ? 'Laddar upp…' : 'Bifoga fil'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleUpload(f);
                  e.target.value = '';
                }}
              />
            </div>

            {data?.attachments.length === 0 && (
              <p className="text-xs text-muted-foreground italic">Inga bilagor ännu.</p>
            )}

            <ul className="space-y-1">
              {data?.attachments.map((att) => (
                <AttachmentRow
                  key={att.id}
                  attachment={att}
                  onDelete={() => handleDeleteAttachment(att)}
                />
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );

  const footer = (
    <DialogFooter>
      <Button variant="ghost" onClick={() => onOpenChange(false)}>Stäng</Button>
      <Button onClick={handleSave} disabled={!dirty || upsert.isPending}>
        {upsert.isPending ? 'Sparar…' : 'Spara'}
      </Button>
    </DialogFooter>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-sm">Detaljer — {headerTitle}</SheetTitle>
            <SheetDescription className="text-xs">
              {entityType} · {entityId}
            </SheetDescription>
          </SheetHeader>
          {detailsCaps.entityDetailsEnabled ? body : upsellBody}
          {detailsCaps.entityDetailsEnabled && (
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Stäng</Button>
              <Button onClick={handleSave} disabled={!dirty || upsert.isPending}>
                {upsert.isPending ? 'Sparar…' : 'Spara'}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detaljer — {headerTitle}</DialogTitle>
          <DialogDescription>
            {entityType} · <code className="text-[11px]">{entityId}</code>
          </DialogDescription>
        </DialogHeader>
        {detailsCaps.entityDetailsEnabled ? body : upsellBody}
        {detailsCaps.entityDetailsEnabled && footer}
      </DialogContent>
    </Dialog>
  );
}

interface AttachmentRowProps {
  attachment: EntityAttachmentRow;
  onDelete: () => void;
}

function AttachmentRow({ attachment, onDelete }: AttachmentRowProps) {
  const sizeKb = useMemo(() => Math.round(attachment.size_bytes / 1024), [attachment.size_bytes]);

  const handleOpen = async () => {
    const url = await getAttachmentSignedUrl(attachment.storage_path);
    if (!url) {
      toast({ title: 'Kunde inte öppna bilagan', variant: 'destructive' });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <li className="flex items-center gap-2 bg-muted/40 rounded p-1.5 text-xs">
      <FileText className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{attachment.filename}</div>
        <div className="text-[10px] text-muted-foreground">
          {attachment.mime_type} · {sizeKb} KB
        </div>
      </div>
      <Button size="icon" variant="ghost" onClick={handleOpen} className="h-6 w-6">
        <Download className="w-3 h-3" />
      </Button>
      <Button size="icon" variant="ghost" onClick={onDelete} className="h-6 w-6 text-destructive">
        <Trash2 className="w-3 h-3" />
      </Button>
    </li>
  );
}
